import {
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';
const DEFAULT_PIN_LENGTH = 10;

export type PinDigitInputProps = {
  value: string;
  onChange: (pin: string) => void;
  disabled?: boolean;
  label?: string;
  hideLabel?: boolean;
  length?: number;
  error?: boolean;
  helperText?: string;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
};

export function PinDigitInput({
  value,
  onChange,
  disabled = false,
  label = 'Authorization PIN',
  hideLabel = false,
  length = DEFAULT_PIN_LENGTH,
  error = false,
  helperText,
  autoFocus = false,
  sx,
}: PinDigitInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pinLength = Math.max(1, length);
  const pin = value.replace(/\D/g, '').slice(0, pinLength);
  const digits = Array.from({ length: pinLength }, (_, i) => pin[i] ?? '');

  const commitPin = (nextDigits: string[]) => {
    onChange(nextDigits.join('').slice(0, pinLength));
  };

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '');

    if (!raw) {
      const next = [...digits];
      next[index] = '';
      commitPin(next);
      return;
    }

    if (raw.length > 1) {
      const merged = [...digits];
      raw.split('').forEach((char, offset) => {
        if (index + offset < pinLength) merged[index + offset] = char;
      });
      onChange(merged.join('').slice(0, pinLength));
      const focusIndex = Math.min(index + raw.length, pinLength - 1);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = raw;
    commitPin(next);
    if (index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        commitPin(next);
        return;
      }
      if (index > 0) {
        const next = [...digits];
        next[index - 1] = '';
        commitPin(next);
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, pinLength);
    if (!pasted) return;
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, pinLength - 1);
    requestAnimationFrame(() => inputRefs.current[focusIndex]?.focus());
  };

  return (
    <Box sx={{ mt: hideLabel ? 0 : 0.5, ...sx }}>
      {!hideLabel && (
        <Typography
          sx={{
            fontSize: '0.72rem',
            color: error ? '#C62828' : '#9B958F',
            mb: 1,
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexWrap: 'nowrap',
          overflowX: 'auto',
        }}
      >
        {digits.map((digit, index) => (
          <Box
            key={index}
            component="input"
            ref={(el: HTMLInputElement | null) => {
              inputRefs.current[index] = el;
              if (autoFocus && index === 0 && el) {
                requestAnimationFrame(() => el.focus());
              }
            }}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={1}
            value={digit}
            disabled={disabled}
            aria-label={`PIN digit ${index + 1}`}
            aria-invalid={error}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(index, event)}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={(event: FocusEvent<HTMLInputElement>) => event.target.select()}
            sx={{
              width: 28,
              height: 36,
              p: 0,
              border: error ? '1px solid #C62828' : BORDER,
              borderRadius: 0,
              bgcolor: '#FFFFFF',
              color: ACCENT,

              fontSize: '0.9rem',
              fontWeight: 600,
              textAlign: 'center',
              outline: 'none',
              flexShrink: 0,
              '&:focus': {
                borderColor: error ? '#C62828' : ACCENT,
              },
              '&:disabled': {
                bgcolor: '#F5F5F5',
                color: '#9B958F',
              },
            }}
          />
        ))}
      </Box>

      {helperText && (
        <Typography
          sx={{
            mt: 0.75,

            fontSize: '0.68rem',
            color: error ? '#C62828' : '#9B958F',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
